import { Button, LuxModal, LuxModalBody, LuxModalFooter, LuxModalHeader } from '@components/ui';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/contexts/AuthContext';
import { useSavReminders } from '@/hooks/useSavReminders';
import { formatElapsed } from '@/services/savRemindersService';
import {
  alertStorageKey,
  canInterruptUser,
  readDismissal,
  shouldOpenAlert,
  writeDismissal,
  type AlertDismissal,
} from '@/utils/savAlertMemory';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RETRY_MS = 3_000;
const RECHECK_MS = 60_000;

/**
 * Fenêtre d'alerte des dossiers SAV non traités (montée une fois dans le gabarit de l'application). Signalés pour tout le personnel ayant le
 * droit « SAV du site » : dès qu'une protection existe (e-mail récent, visite programmée...), le dossier sort de la liste pour tous.
 * Fermée sur un poste, elle ne revient pas avant le rythme réglé (4 h), sauf si un nouveau dossier entre dans l'alerte. Elle ne
 * s'ouvre pas pendant une autre fenêtre ni pendant la saisie. Elle n'envoie jamais rien au client.
 */
export function SavAlertModal() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const { allowed, alerts, alertRepeatHours } = useSavReminders();

  const [dismissal, setDismissal] = useState<AlertDismissal | null>(null);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);

  // Mémoire de ce poste pour cet utilisateur (rechargée quand l'utilisateur change ou quand un autre onglet ferme la fenêtre)
  useEffect(() => {
    setDismissal(userId ? readDismissal(userId) : null);
    setOpen(false);
  }, [userId]);
  useEffect(() => {
    if (!userId) return;
    const key = alertStorageKey(userId);
    const onStorage = (e: StorageEvent) => {
      if (e.key === key || e.key === null) setDismissal(readDismissal(userId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  // Réévaluation régulière : le rythme de 4 h arrive à échéance même sans nouvelle donnée
  useEffect(() => {
    if (!allowed) return;
    const t = window.setInterval(() => setTick(n => n + 1), RECHECK_MS);
    return () => window.clearInterval(t);
  }, [allowed]);

  const alertIds = useMemo(() => alerts.map(a => a.request_id), [alerts]);
  const due =
    allowed &&
    shouldOpenAlert({ alertIds, dismissal, now: Date.now(), repeatHours: alertRepeatHours });

  useEffect(() => {
    if (!allowed || alertIds.length === 0) {
      setOpen(false);
      return;
    }
    if (open) {
      // fermée ailleurs (autre onglet) : elle se ferme ici aussi
      if (!due) setOpen(false);
      return;
    }
    if (!due) return;
    if (canInterruptUser()) {
      setOpen(true);
      return;
    }
    const t = window.setTimeout(() => setTick(n => n + 1), RETRY_MS); // une autre fenêtre ou une saisie est en cours : on réessaie
    return () => window.clearTimeout(t);
  }, [allowed, alertIds.length, due, open, tick]);

  const dismiss = useCallback(() => {
    const next: AlertDismissal = { at: Date.now(), ids: alertIds };
    setDismissal(next);
    if (userId) writeDismissal(userId, next);
    setOpen(false);
  }, [alertIds, userId]);

  if (!allowed || !open || alerts.length === 0) return null;

  return (
    <LuxModal isOpen onClose={dismiss} size="xl" showCloseButton>
      <LuxModalHeader showCloseButton onClose={dismiss}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 p-2 rounded-lg bg-amber-100">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-700" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Dossiers SAV à traiter</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {alerts.length} dossier{alerts.length > 1 ? 's' : ''} sans traitement établi
            </p>
          </div>
        </div>
      </LuxModalHeader>
      <LuxModalBody className="space-y-3">
        <p className="text-sm text-ink">
          Ces dossiers n'ont ni e-mail récent du personnel, ni visite programmée, ni traitement en
          cours. Écrivez au client, programmez une visite ou clôturez le dossier pour le sortir de
          cette liste (pour tout le monde).
        </p>
        <ul className="max-h-[50vh] overflow-y-auto divide-y divide-default rounded-xl border border-default">
          {alerts.map(a => (
            <li key={a.request_id}>
              <button
                type="button"
                data-testid="sav-alert-row"
                onClick={() => {
                  dismiss();
                  navigate(`${ROUTES.SAV_REQUESTS}?d=${a.request_id}`);
                }}
                className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-token-neutral-bg-hover focus:outline-none focus:bg-token-neutral-bg-hover transition-colors"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">{a.reference}</span>
                  <span className="block text-xs text-muted [overflow-wrap:anywhere]">
                    {a.client_name}
                    {a.store_name ? ` · ${a.store_name}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-xs font-medium text-amber-800">
                  Sans traitement depuis {formatElapsed(a.elapsed_hours)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </LuxModalBody>
      <LuxModalFooter align="right">
        <span className="mr-auto text-xs text-muted">
          Fermée, cette fenêtre revient dans {alertRepeatHours} h (plus tôt si un nouveau dossier
          est signalé).
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={dismiss}
          className="whitespace-nowrap"
        >
          Fermer
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="whitespace-nowrap"
          onClick={() => {
            dismiss();
            navigate(ROUTES.SAV_REQUESTS);
          }}
        >
          Ouvrir les demandes SAV
        </Button>
      </LuxModalFooter>
    </LuxModal>
  );
}
